function createElement({ type = 'DIV', classNames = [], attributes = null, parent = null, content = null, children = [], } = {}) {
    const e = document.createElement(type);
    if (classNames.length) {
        e.classList.add(...classNames);
    }
    if (attributes) {
        Object.entries(attributes).forEach(([name, value]) => {
            e.setAttribute(name, value);
        });
    }
    if (content) {
        e.innerText = content;
    }
    if (children.length) {
        e.append(...children);
    }
    if (parent) {
        parent.appendChild(e);
    }
    return e;
}

class EnhancedElement {
    element;
    visible = true;
    // for connected elements:
    unsubscribe = null;
    store = null;
    data = new Map();
    static fromElement(element) {
        return new EnhancedElement(element);
    }
    static fromElementType(elementType) {
        const element = document.createElement(elementType);
        return new EnhancedElement(element);
    }
    static fromNewDiv() {
        return EnhancedElement.fromElementType('div');
    }
    static fromDefinition(definition) {
        const element = createElement(definition);
        return new EnhancedElement(element);
    }
    static fromSelector(selector, searchRoot = document) {
        const element = searchRoot.querySelector(selector);
        if (element) {
            return new EnhancedElement(element);
        }
        return null;
    }
    static fromSelectorAll(selector, searchRoot = document) {
        const elements = searchRoot.querySelectorAll(selector);
        return Array.from(elements).map((element) => new EnhancedElement(element));
    }
    static enhance(selector, Component, store = null, ...additionalArgs) {
        const element = document.querySelector(selector);
        if (element) {
            const enhanced = new Component(element, ...additionalArgs);
            if (store) {
                enhanced.connect(store);
            }
            return enhanced;
        }
        return null;
    }
    static enhanceAll(selector, Component, store = null, ...additionalArgs) {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).map((e) => {
            const enhanced = new Component(e, ...additionalArgs);
            if (store) {
                enhanced.connect(store);
            }
            return enhanced;
        });
    }
    constructor(element) {
        this.element = element;
        const classes = this.getClassNames();
        if (this.element.classList.contains(classes.hidden)) {
            this.visible = false;
            this.setAttribute('aria-hidden', 'true');
        }
    }
    /**
     * Use this to connect your element to a subscribable store.
     */
    connect(store) {
        this.store = store;
        this.unsubscribe = store.subscribe(this.onStateUpdate.bind(this));
        this.onAfterConnect();
        return this;
    }
    onAfterConnect() { }
    getElement() {
        return this.element;
    }
    /**
     * @return {() => void}  An "unsubscriber" function to remove the event listener again.
     */
    on(eventName, callback, options) {
        this.element.addEventListener(eventName, callback, options);
        return () => {
            this.element.removeEventListener(eventName, callback, options);
        };
    }
    show() {
        if (this.visible)
            return this;
        const classes = this.getClassNames();
        this.removeClass(classes.hidden);
        this.setAttribute('aria-hidden', 'false');
        this.visible = true;
        return this;
    }
    hide() {
        if (!this.visible)
            return this;
        const classes = this.getClassNames();
        this.addClass(classes.hidden);
        this.setAttribute('aria-hidden', 'true');
        this.visible = false;
        return this;
    }
    content(innerText) {
        if (innerText !== this.element.innerText) {
            this.element.innerText = innerText;
        }
        return this;
    }
    contentOrHide(innerText = null) {
        if (!innerText) {
            this.hide();
            return this;
        }
        return this.content(innerText);
    }
    getContent() {
        return this.element.innerText;
    }
    addClass(...classes) {
        this.element.classList.add(...classes);
        return this;
    }
    removeClass(...classes) {
        this.element.classList.remove(...classes);
        return this;
    }
    appendTo(parent) {
        if (parent instanceof EnhancedElement) {
            parent.getElement().appendChild(this.element);
        }
        else {
            parent.appendChild(this.element);
        }
        return this;
    }
    insertAfter(element) {
        const elementUsed = element instanceof EnhancedElement ? element.getElement() : element;
        const parent = elementUsed.parentElement;
        if (elementUsed.nextElementSibling !== null) {
            parent.insertBefore(this.element, elementUsed.nextElementSibling);
        }
        else {
            parent.appendChild(this.element);
        }
        return this;
    }
    destroy() {
        const classes = this.getClassNames();
        this.element.classList.add(classes.hidden);
        this.element.remove();
        if (this.store) {
            this.store = null;
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }
    }
    clone() {
        const clonedElement = this.element.cloneNode(true);
        return new EnhancedElement(clonedElement);
    }
    cloneInto(constructor, ...additionalArgs) {
        const clonedElement = this.element.cloneNode(true);
        return new constructor(clonedElement, ...additionalArgs);
    }
    isVisible() {
        return this.visible;
    }
    find(selector) {
        return this.element.querySelector(selector);
    }
    findAll(selector, asArray = false) {
        const nodeList = this.element.querySelectorAll(selector);
        return asArray ? Array.from(nodeList) : nodeList;
    }
    findAndWrap(selector) {
        const baseElement = this.find(selector);
        return baseElement
            ? new EnhancedElement(baseElement)
            : null;
    }
    findAndWrapAll(selector, store = null) {
        return this.findAll(selector, true).map((e) => {
            const enhanced = new EnhancedElement(e);
            if (store) {
                enhanced.connect(store);
            }
            return enhanced;
        });
    }
    findAndWrapInto(selector, into, store = null, ...additionalArgs) {
        const baseElement = this.find(selector);
        if (baseElement) {
            const enhanced = new into(baseElement, ...additionalArgs);
            if (store) {
                enhanced.connect(store);
            }
            return enhanced;
        }
        return null;
    }
    findAndWrapAllInto(selector, into, store = null, ...additionalArgs) {
        return this.findAll(selector, true).map((e) => {
            const enhanced = new into(e, ...additionalArgs);
            if (store) {
                enhanced.connect(store);
            }
            return enhanced;
        });
    }
    parent(selector = null) {
        if (selector) {
            let parent = null;
            let nextParent = this.element.parentElement;
            while (parent === null && nextParent !== document.body) {
                if (nextParent.matches(selector)) {
                    parent = nextParent;
                }
                nextParent = nextParent.parentElement;
            }
            return parent;
        }
        return this.element.parentElement;
    }
    get getAttribute() {
        return this.element.getAttribute;
    }
    setAttribute(attributeName, attributeValue) {
        this.element.setAttribute(attributeName, attributeValue);
        return this;
    }
    get dataset() {
        return this.element.dataset;
    }
    getData(key) {
        return this.data.get(key);
    }
    setData(key, value) {
        this.data.set(key, value);
        return this;
    }
    getClassNames() {
        return {
            hidden: 'hidden',
        };
    }
    emit(eventName, data) {
        this.element.dispatchEvent(new CustomEvent(eventName, {
            detail: data,
        }));
    }
    onStateUpdate(state) {
        this.element.dispatchEvent(new CustomEvent('state-update', {
            detail: state,
        }));
    }
}
function energize(selector, energizer = null, store = null, ...additionalArgs) {
    if (!energizer ||
        energizer instanceof Element ||
        energizer instanceof Document) {
        return EnhancedElement.fromSelector(selector, energizer);
    }
    return EnhancedElement.enhance(selector, energizer, store, ...additionalArgs);
}
function energizeAll(selector, energizer = null, store = null, ...additionalArgs) {
    if (!energizer ||
        energizer instanceof Element ||
        energizer instanceof Document) {
        return EnhancedElement.fromSelectorAll(selector, energizer);
    }
    return EnhancedElement.enhanceAll(selector, energizer, store, ...additionalArgs);
}

export { EnhancedElement, energize, energizeAll };
//# sourceMappingURL=index.js.map
